import { useQueryClient } from '@tanstack/react-query';
import { useApi, ApiError } from '@/hooks/useApi';
import { useModeAwareMutation } from '@/hooks/useModeAwareQuery';

export interface BulkUpdateCustomersRequest {
  customerIds: number[];
  collectionStrategyId?: number;
  creditControllerId?: number;
  accountManagerId?: number;
  riskLevelId?: number;
}

interface BulkUpdateCustomersResponse {
  updated: number;
  message: string;
}

export function useBulkUpdateCustomers() {
  const { apiBaseUrl, getHeaders } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<BulkUpdateCustomersResponse, Error, BulkUpdateCustomersRequest>(
    async (request) => {
      const headers = await getHeaders();
      const response = await fetch(`${apiBaseUrl}/api/v1/customers/bulk-update`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError(
          errorData.message || `HTTP error ${response.status}`,
          response.status,
          errorData
        );
      }

      return response.json();
    },
    (request) => {
      return {
        updated: request.customerIds.length,
        message: `${request.customerIds.length} customer(s) updated successfully`,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["customers"] });
        queryClient.invalidateQueries({ queryKey: ["customers-dashboard"] });
        queryClient.invalidateQueries({ queryKey: ["customer-audit-logs"] });
      },
    }
  );
}
